// src/widgets/Top10Widget.jsx
//
// Widget ranking Top-N yang bisa dipakai untuk:
//   widget="top10_username"     → ranking berdasarkan usage bytes
//   widget="top10_nas"          → ranking NAS IP
//   widget="top10_realm"        → ranking realm
//   widget="top_profile"        → ranking Profile (field "profile")
//   widget="top_radius_server"  → ranking Radius Server
//
// Props:
//   widget      {string}  - nama widget (top10_username | top10_nas | top10_realm | top_profile | top_radius_server)
//   filter      {object}  - { period, radius_server_id, tenant_id, realm, limit }
//   title       {string}  - judul widget (optional, ada default)
//   keyField    {string}  - field key yang ditampilkan (default sesuai widget)

import useWidgetData from '../hooks/useWidgetData';

const WIDGET_DEFAULTS = {
  top10_username:    { title: 'Top Username',      keyField: 'username',     color: 'var(--accent-blue)' },
  top10_nas:         { title: 'Top NAS Device',     keyField: 'nasipaddress', color: 'var(--accent-cyan)' },
  top10_realm:       { title: 'Top Realm',          keyField: 'realm',        color: 'var(--accent-purple, #a78bfa)' },
  top_profile:       { title: 'Top Profile',        keyField: 'profile',      color: 'var(--accent-orange, #fb923c)' },
  top_radius_server: { title: 'Top Radius Server',  keyField: 'server_name',  color: 'var(--accent-green, #22c55e)' },
};

function fmtBytes(bytes) {
  const n = Number(bytes) || 0;
  if (n === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(n) / Math.log(1024));
  return `${(n / 1024 ** i).toFixed(1)} ${units[Math.min(i, units.length - 1)]}`;
}

export default function Top10Widget({ widget = 'top10_username', filter = {}, title, keyField }) {
  const meta    = WIDGET_DEFAULTS[widget] || WIDGET_DEFAULTS.top10_username;
  const label   = title    || meta.title;
  const rowKey  = keyField || meta.keyField;
  const color   = meta.color;

  const { data, loading, error } = useWidgetData(widget, filter);
  const rows = Array.isArray(data) ? data : [];

  const maxBytes = rows.length > 0 ? Number(rows[0]?.total_bytes || 0) : 1;

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'var(--bg-card)', border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)', padding: '16px 18px', boxSizing: 'border-box',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {label}
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
          Memuat…
        </div>
      ) : error ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-red)', fontSize: 12, textAlign: 'center', padding: 8 }}>
          {error}
        </div>
      ) : rows.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
          Tidak ada data
        </div>
      ) : (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((row, i) => {
            const pct = Math.max(2, (Number(row.total_bytes || 0) / maxBytes) * 100);
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <span style={{
                      width: 18, height: 18, borderRadius: 4, background: i < 3 ? color : 'var(--bg-input)',
                      color: i < 3 ? '#fff' : 'var(--text-muted)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 10, fontWeight: 700, flexShrink: 0,
                    }}>{i + 1}</span>
                    <span style={{
                      fontSize: 12, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={row[rowKey]}>
                      {row[rowKey] || '—'}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, marginLeft: 8 }}>
                    {fmtBytes(row.total_bytes)}
                  </span>
                </div>
                {/* Progress bar */}
                <div style={{ height: 3, background: 'var(--bg-input)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 2, transition: 'width 0.4s ease' }} />
                </div>
                {/* Sub info */}
                <div style={{ display: 'flex', gap: 12, paddingLeft: 26 }}>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>↑ {fmtBytes(row.upload_bytes)}</span>
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>↓ {fmtBytes(row.download_bytes)}</span>
                  {row.total_sessions && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{row.total_sessions} sesi</span>
                  )}
                  {row.unique_users && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{row.unique_users} user</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}